// The happens-before graph of a run: one node per trace event — or per
// *transaction* — and one edge per immediate causal predecessor.
//
// This is the partial order the whole causality machinery is *about* — program
// order within a thread, plus the cross-thread edges (a read from the write it
// observed, a child from the spawn that made it, an acquire from the release
// that freed the lock, a retry re-attempt from the commit that woke it). Two
// events with no path between them are concurrent, which is exactly the relation
// a partial-order reduction reasons over: an edge here is a *dependency*, and its
// absence is the freedom to reorder. See docs/design/happens-before.md.
//
// **A transaction is one node.** Everything between a `txn`'s begin and the
// commit (or abort, or retry-park) that ends it is drawn collapsed, because that
// is the granularity the language actually enforces: nothing inside an attempt
// is separately observable, and the edges that matter — a transaction reading
// what another committed — are between the *attempts*, not between the loads.
// `bank.stm` goes from 106 nodes to a couple of dozen, and what is left reads as
// what it is: a handful of transfers, with the reads-from edges that ordered
// them. Click one to expand it back into its events. See
// docs/design/txn-granular-exploration.md, which proposed exactly this rendering.
//
// **Thread-local accesses are not drawn.** An address only one thread ever
// touched cannot be part of a race and cannot change any other thread's view of
// anything, so it is not part of the concurrency story and its accesses are not
// nodes here — the program-order edges splice through them, so each thread's
// spine stays connected. A library's internal bookkeeping (the dictionary a
// spawn group keeps its futures in, the accumulator a fold builds) is heap
// traffic like any other, and drawing every bit of it is noise with a node per
// access. See docs/design/thread-local-state.md.
//
// Pure and layout-free. `causality.deps` already holds the immediate predecessors
// (the Hasse diagram of the order, not its transitive closure), so this only
// classifies each edge by *why* it exists — the interpreter records the edges as
// bare indices, so the kind is re-derived from the two events' types, for colour.

import type { RunResult } from "../src/backend/interp/interpreter.js";
import { eventThread } from "../src/backend/interp/causality.js";
import type { TraceEvent } from "../src/backend/interp/trace.js";
import { privateAddresses } from "./conflict.js";
import { eventColumns } from "./trace-layout.js";
import type { ObjectTypes } from "./trace-layout.js";

/**
 * One attempt at a `txn`: its rows, and how it finished.
 *
 * An *attempt*, not a transaction: a retry gets a fresh id, so each attempt is
 * its own node and an aborted one is visible as an abort rather than folded into
 * the attempt that succeeded. That is the honest picture — the abort really
 * happened, and the commit that doomed it is one of this node's incoming edges.
 */
export interface HBTxn {
  id: number;
  /** Trace rows it covers, ascending: the begin, its accesses and locks, and the
   *  event that ended it. */
  rows: number[];
  /** `open` when the run ended before this attempt did. */
  ended: "commit" | "abort" | "retry" | "open";
  /** Whether it is drawn as one node here, or as its own events. */
  collapsed: boolean;
}

/** One event, as a node — or one whole transaction attempt, when `txn` is set.
 *  `index` is a trace-row index (a transaction's is its `begin` row), the anchor
 *  the panel selects on and the graph↔trace bridge keys off. */
export interface HBNode {
  index: number;
  thread: number;
  /** Short op name (`read`, `write`, `txn#3`, …) and its colour class. */
  op: string;
  opCls: string;
  /** The address or target, when the op has one (`/account/1`, `→ T2`). */
  detail: string;
  /** The attempt this node stands for, when it stands for one. */
  txn?: HBTxn;
}

/**
 * Why one event causes another — for colour, and for reading the graph:
 *  - `program`   same thread, in order — the spine of each thread;
 *  - `reads-from` a read observing another thread's write — the edge a race is;
 *  - `fork`      a spawn to the child thread's first event;
 *  - `wake`      a commit waking a `retry`-parked re-attempt;
 *  - `sync`      any other cross-thread edge (a lock handoff, a validation).
 */
export type HBEdgeKind = "program" | "reads-from" | "fork" | "wake" | "sync" | "start";

export interface HBEdge {
  from: number;
  to: number;
  kind: HBEdgeKind;
}

/** The synthetic source node's index — the one node that is not a trace event.
 *  Negative so it sorts before every real event (and so `from < to` still holds
 *  for its edges, which the layout relies on). */
export const PROGRAM_START = -1;

export interface HBGraph {
  nodes: HBNode[];
  edges: HBEdge[];
  /** Distinct threads, ascending — for a legend / lane colouring. */
  threads: number[];
  /** Every transaction attempt in the run, collapsed or not — so the panel can
   *  draw a box around the ones it expanded, and offer to collapse them again. */
  txns: HBTxn[];
  /** The trace rows left out of the drawing, ascending: accesses to addresses
   *  only one thread ever touched. Their causal edges splice through, so the
   *  order that remains is the same order, minus the parts of it no other thread
   *  could see. Reported so the panel can say how many, rather than quietly
   *  drawing a smaller graph. */
  elided: number[];
}

/** Classify the edge `a → b` (a is an immediate cause of b) by the two events'
 *  types. Derived, because the interpreter stores edges as bare indices. */
function edgeKind(result: RunResult, a: number, b: number): HBEdgeKind {
  const events = result.trace.events;
  const ea = events[a]!;
  const eb = events[b]!;
  const sameThread = eventThread(ea) === eventThread(eb);
  if (ea.type === "spawn" && eb.type === "thread-start") return "fork";
  if (ea.type === "txn-commit" && eb.type === "txn-begin" && !sameThread) return "wake";
  if (eb.type === "read" && !sameThread) return "reads-from";
  if (sameThread) return "program";
  return "sync";
}

/** The transaction attempt an event belongs to, or null if it belongs to none.
 *  Everything an attempt does carries its id — its accesses, its locks, and the
 *  four events that bracket it. */
function txnOf(ev: TraceEvent): number | null {
  switch (ev.type) {
    case "txn-begin":
    case "txn-commit":
    case "txn-abort":
    case "txn-retry":
    case "orelse-retry":
    case "lock-acquire":
    case "lock-wait":
    case "lock-release":
      return ev.txn;
    case "read":
    case "write":
      return ev.txn === "auto" ? null : ev.txn;
    default:
      return null;
  }
}

/** Which kind survives when several edges collapse onto one pair: the most
 *  informative. A transaction reading what another committed is the edge worth
 *  seeing; that it also ran after it in program order is not. */
const EDGE_RANK: Record<HBEdgeKind, number> = {
  "reads-from": 5,
  wake: 4,
  sync: 3,
  fork: 2,
  program: 1,
  start: 0,
};

/**
 * Build the happens-before graph, or null when the run carried no causality.
 *
 * `expanded` names the transaction attempts to draw as their own events instead
 * of as one node — the ones the user opened.
 *
 * Accesses to addresses only one thread ever touched are left out (`elided`);
 * everything else about the order is drawn, spliced through them.
 */
export function buildHBGraph(
  result: RunResult,
  types?: ObjectTypes,
  expanded?: ReadonlySet<number>
): HBGraph | null {
  const c = result.causality;
  if (!c) return null;
  const events = result.trace.events;

  // Group each attempt's rows. An expanded one is still recorded — the panel
  // draws a box around it — but its rows are left to become their own nodes.
  const attempts = new Map<number, HBTxn>();
  const groupOf = new Map<number, number>();
  events.forEach((ev, row) => {
    const id = txnOf(ev);
    if (id === null) return;
    const attempt = attempts.get(id) ?? { id, rows: [], ended: "open" as const, collapsed: !expanded?.has(id) };
    attempt.rows.push(row);
    if (ev.type === "txn-commit") attempt.ended = "commit";
    else if (ev.type === "txn-abort") attempt.ended = "abort";
    else if (ev.type === "txn-retry") attempt.ended = "retry";
    attempts.set(id, attempt);
    if (attempt.collapsed) groupOf.set(row, id);
  });

  /** The node an event is drawn as: itself, or the attempt that swallowed it. */
  const nodeOf = (row: number): number => {
    const id = groupOf.get(row);
    return id === undefined ? row : attempts.get(id)!.rows[0]!;
  };

  // The thread-local rule: an access to an address only one thread ever touched
  // is not drawn. Only accesses that would be their own node — one inside a
  // collapsed attempt is already folded into it, and a transaction is one node
  // either way.
  const priv = privateAddresses(events);
  const elided = new Set<number>();
  events.forEach((ev, row) => {
    if (ev.type !== "read" && ev.type !== "write") return;
    if (groupOf.has(row)) return;
    if (priv.has(String(ev.addr))) elided.add(row);
  });

  const nodes: HBNode[] = [];
  events.forEach((ev, row) => {
    if (elided.has(row)) return;
    const id = groupOf.get(row);
    if (id === undefined) {
      const cols = eventColumns(ev, types);
      // A compact target: the key for a memory op, or the tail of the value for
      // spawn / start (`→ T2 …`, `fn()`), so the node reads without its row.
      const detail = cols.key || (ev.type === "spawn" ? `→ T${ev.child}` : "");
      nodes.push({ index: row, thread: eventThread(ev), op: cols.op, opCls: cols.opCls, detail });
      return;
    }
    const attempt = attempts.get(id)!;
    if (attempt.rows[0] !== row) return; // a later row of an attempt already drawn
    // The colour is the attempt's *fate* — a commit is green and an abort red
    // wherever else they appear, and a node standing for the whole attempt should
    // read the same way. Taken from how it ended rather than from its last row,
    // which under the `hybrid` strategy is the lock release *after* the commit.
    nodes.push({
      index: row,
      thread: eventThread(ev),
      op: `txn#${id}`,
      opCls: attempt.ended === "commit" ? "op-commit" : attempt.ended === "open" ? "op-txn" : "op-abort",
      detail: attempt.ended,
      txn: attempt,
    });
  });

  // The causes of each row that are still drawn: its own, with an elided one
  // replaced by *its* causes — the splice, so a thread's spine survives having
  // its private accesses taken out of it. `deps` only ever names earlier rows, so
  // one forward pass fills this in.
  //
  // Splicing has to *thin* as it goes, or it doesn't reduce anything. A run of
  // elided accesses is not a chain: a read depends on the write it observed as
  // well as on the row before it, so following both branches back reaches several
  // survivors, and every one of them would become an edge — `map_reduce` came out
  // as 12 nodes joined by 40 arcs, each a longer way round the spine. So a spliced
  // cause another cause already reaches is dropped: what is drawn is the
  // *immediate* visible predecessors, which is what `deps` is for the un-elided
  // graph. An edge the interpreter itself recorded is never thinned — with nothing
  // elided this is exactly `deps`, unchanged.
  //
  // `anc` is the visible ancestors of each row, as a bitset over the drawn rows,
  // which is what "already reaches" is asked of.
  const vid = new Map<number, number>();
  events.forEach((_ev, row) => {
    if (!elided.has(row)) vid.set(row, vid.size);
  });
  const words = Math.ceil(Math.max(1, vid.size) / 32);
  const anc: Uint32Array[] = [];
  const causes: number[][] = [];
  /** Whether `row` has the drawn row `target` among its causal ancestors. */
  const reaches = (row: number, target: number): boolean => {
    const id = vid.get(target)!;
    return (anc[row]![id >>> 5]! & (1 << (id & 31))) !== 0;
  };
  events.forEach((_ev, row) => {
    const reach = new Uint32Array(words);
    const direct = new Set<number>();
    const raw: number[] = [];
    for (const from of c.deps[row] ?? []) {
      const up = anc[from];
      if (up) for (let w = 0; w < words; w++) reach[w]! |= up[w]!;
      const id = vid.get(from);
      if (id !== undefined) {
        reach[id >>> 5]! |= 1 << (id & 31);
        direct.add(from);
        if (!raw.includes(from)) raw.push(from);
      } else {
        for (const spliced of causes[from] ?? []) if (!raw.includes(spliced)) raw.push(spliced);
      }
    }
    anc[row] = reach;
    causes[row] = raw.filter((x) => direct.has(x) || !raw.some((y) => y !== x && reaches(y, x)));
  });

  // One edge per causal pair, with both ends moved to the node that draws them.
  // A pair that lands inside one attempt disappears — that is the collapse — and
  // several that land on the same pair of nodes become one.
  const merged = new Map<string, HBEdge>();
  causes.forEach((preds, to) => {
    if (elided.has(to)) return; // its causes were handed to whatever it caused
    for (const from of preds) {
      const a = nodeOf(from);
      const b = nodeOf(to);
      if (a === b) continue; // inside one attempt: not an ordering between things
      const kind = edgeKind(result, from, to);
      const key = `${a}->${b}`;
      const prior = merged.get(key);
      if (!prior || EDGE_RANK[kind] > EDGE_RANK[prior.kind]) merged.set(key, { from: a, to: b, kind });
    }
  });
  const edges: HBEdge[] = [...merged.values()];

  const threads = [...new Set(nodes.map((n) => n.thread))].sort((a, b) => a - b);

  // A single labelled source above the whole order — the program's beginning,
  // before any thread has run. It points at the *roots* of the causal order: the
  // events with no cause, which for an ordinary program is `main`'s first step,
  // whence everything (the spawns included) descends. Without it the graph's
  // origin is just another `start` node, indistinguishable from a thread's.
  const hasIncoming = new Set(edges.map((e) => e.to));
  const roots = nodes.filter((n) => !hasIncoming.has(n.index));
  nodes.push({ index: PROGRAM_START, thread: -1, op: "program start", opCls: "", detail: "" });
  for (const r of roots) edges.push({ from: PROGRAM_START, to: r.index, kind: "start" });

  return { nodes, edges, threads, txns: [...attempts.values()], elided: [...elided] };
}
